import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { currentFiscalYear, formatFY } from "@/lib/fiscal";
import {
  CopyToFYSelect,
  FP_METRIC_PREFIX,
  FP_METRICS,
  type GoalRow,
  GoalRowInput,
  SectionHeader,
  useSaveFlash,
} from "./shared";

export function FPReportingGoalsSection({
  ownerId,
  fiscalYear,
  onDataChange,
  onRemove,
  readOnly = false,
}: {
  ownerId: string;
  fiscalYear: number;
  onDataChange: (hasData: boolean) => void;
  onRemove?: () => void;
  readOnly?: boolean;
}) {
  const currentFY = currentFiscalYear();
  const [goals, setGoals] = useState<GoalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const { savedField, flashSaved } = useSaveFlash();
  const lastSavedRef = useRef<Record<string, string>>({});

  // Fetch targets
  const fetchTargets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/owners/${ownerId}/targets?fy=${fiscalYear}`);
      if (res.ok) {
        const data = await res.json();
        const rows: GoalRow[] = data.targets
          .filter((t: { metric: string }) => t.metric.startsWith(FP_METRIC_PREFIX))
          .map((t: { metric: string; targetValue: number }) => ({
            metric: t.metric,
            value: String(t.targetValue),
          }));
        setGoals(rows);
        const snapshot: Record<string, string> = {};
        for (const r of rows) snapshot[r.metric] = r.value;
        lastSavedRef.current = snapshot;
        onDataChange(rows.length > 0);
      }
    } finally {
      setLoading(false);
    }
  }, [ownerId, fiscalYear, onDataChange]);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  // Always show all FP metrics in order
  const allGoals: GoalRow[] = FP_METRICS.map((m) => {
    const existing = goals.find((g) => g.metric === m.key);
    return existing ?? { metric: m.key, value: "" };
  });

  function updateGoalValue(metric: string, value: string) {
    setGoals((prev) => {
      const exists = prev.find((g) => g.metric === metric);
      if (exists) {
        return prev.map((g) => (g.metric === metric ? { ...g, value } : g));
      }
      return [...prev, { metric, value }];
    });
  }

  const saveMetric = useCallback(
    async (metric: string, value: string) => {
      if (value === (lastSavedRef.current[metric] ?? "")) return;

      const res = await fetch(`/api/owners/${ownerId}/targets`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fiscalYear,
          targets: [{ metric, targetValue: value === "" ? null : Number(value) }],
        }),
      });

      if (res.ok) {
        lastSavedRef.current[metric] = value;
        flashSaved(metric);
        setGoals((prev) => {
          const exists = prev.find((g) => g.metric === metric);
          if (value === "") {
            const updated = prev.filter((g) => g.metric !== metric);
            onDataChange(updated.length > 0);
            return updated;
          }
          if (exists) {
            onDataChange(true);
            return prev;
          }
          onDataChange(true);
          return [...prev, { metric, value }];
        });
      }
    },
    [ownerId, fiscalYear, flashSaved, onDataChange],
  );

  function handleBlur(metric: string) {
    const goal = allGoals.find((g) => g.metric === metric);
    if (goal) saveMetric(metric, goal.value);
  }

  async function removeAll() {
    const toDelete = goals.map((g) => ({ metric: g.metric, targetValue: null }));
    if (toDelete.length > 0) {
      await fetch(`/api/owners/${ownerId}/targets`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fiscalYear, targets: toDelete }),
      });
    }
    setGoals([]);
    lastSavedRef.current = {};
    onDataChange(false);
    onRemove?.();
  }

  async function copyToFY(targetFY: number) {
    if (goals.length === 0) return;
    await fetch(`/api/owners/${ownerId}/targets`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fiscalYear: targetFY,
        targets: goals
          .filter((g) => g.value !== "")
          .map((g) => ({ metric: g.metric, targetValue: Number(g.value) })),
      }),
    });
  }

  return (
    <Card>
      <CardContent className="pt-5">
        <SectionHeader title="FP Reporting Goals" onRemove={readOnly ? undefined : removeAll}>
          {!readOnly && goals.length > 0 && (
            <CopyToFYSelect currentFY={currentFY} selectedFY={fiscalYear} onCopy={copyToFY} />
          )}
        </SectionHeader>

        <div className={loading ? "opacity-60" : ""}>
          {!loading && goals.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              No FP reporting goals configured for {formatFY(fiscalYear)}.
              {!readOnly && " Set targets below."}
            </p>
          ) : null}
          <div className="space-y-3">
            {allGoals.map((goal) => (
              <GoalRowInput
                key={goal.metric}
                goal={goal}
                onChangeValue={(v) => updateGoalValue(goal.metric, v)}
                onBlur={() => handleBlur(goal.metric)}
                savedField={savedField}
                disabled={readOnly}
              />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
